import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { supabase } from "../Supabase";
import {
  saveInvoiceToDb,
  getInvoiceById,
  updateInvoiceInDb,
} from "../WriteToDb";
import { createHtml } from "../makeInvoiceHtml";
import { getJobsForUser, createJob } from "../data/JobsDb";
import {
  getCustomersForUser,
  createCustomer,
  updateCustomer,
} from "../data/CustomersDb";
import { useNotifications } from "../components/NotificationCenter";
import { INPUT_LIMITS, clampFields, clampText } from "../lib/inputLimits";

const LOGO_BUCKET = "logos";
const limitText = (value, key) =>
  clampText(value ?? "", INPUT_LIMITS[key] ?? 255);
const INVOICE_FIELD_LIMITS = {
  companyName: INPUT_LIMITS.companyName,
  address: INPUT_LIMITS.address,
  city: INPUT_LIMITS.city,
  postCode: INPUT_LIMITS.postCode,
  invoiceNumber: INPUT_LIMITS.invoiceNumber,
  customerName: INPUT_LIMITS.customerName,
  customerAddress: INPUT_LIMITS.customerAddress,
  customerCity: INPUT_LIMITS.customerCity,
  customerPostCode: INPUT_LIMITS.customerPostCode,
  phoneNumber: INPUT_LIMITS.phoneNumber,
  email: INPUT_LIMITS.email,
  payee: INPUT_LIMITS.payee,
  sortCode: INPUT_LIMITS.sortCode,
  accountNumber: INPUT_LIMITS.accountNumber,
  notes: INPUT_LIMITS.notes,
  terms: INPUT_LIMITS.terms,
};

const clampInvoiceRecord = (record) => {
  const base = clampFields(record, INVOICE_FIELD_LIMITS);
  const items = Array.isArray(record.items) ? record.items : [];
  base.items = items.map((it) => ({
    ...it,
    description: limitText(String(it?.description ?? ""), "itemDescription"),
    jobName: it?.jobName
      ? limitText(String(it.jobName), "jobName")
      : it?.jobName,
  }));
  base.notes = limitText(base.notes ?? "", "notes");
  base.terms = limitText(base.terms ?? "", "terms");
  return base;
};

const STYLE_CHOICES = ["formal", "modern", "corporate", "creative", "tech", "old"];

const n = (v, def = 0) => (Number.isFinite(Number(v)) ? Number(v) : def);

const fmtDate = (d) => (d && d.trim() ? d : dayjs().format("YYYY-MM-DD"));

const rid = () => Math.random().toString(36).slice(2);

const normalizeItems = (maybe) => {
  const arr = Array.isArray(maybe) ? maybe : [];
  return arr.map((it) => ({
    id: String(it?.id ?? rid()),
    description: limitText(String(it?.description ?? ""), "itemDescription"),
    qty: n(it?.qty, 0),
    unitPrice: n(it?.unitPrice ?? it?.unit_price, 0),
    jobId: it?.jobId ?? null,
    jobName: it?.jobName
      ? limitText(String(it.jobName), "jobName")
      : it?.jobName ?? null,
  }));
};

const newItem = () => ({
  id: rid(),
  description: "",
  qty: 1,
  unitPrice: 0,
});

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

function extractStoragePath(raw) {
  if (!raw) return null;

  if (/^data:|^blob:/.test(raw)) {
    return null;
  }

  if (/^https?:\/\//.test(raw)) {
    try {
      const url = new URL(raw);
      const prefix = `/storage/v1/object/public/${LOGO_BUCKET}/`;
      const idx = url.pathname.indexOf(prefix);
      if (idx === -1) {
        console.warn("[logo] Unexpected public URL format:", raw);
        return null;
      }
      return url.pathname.slice(idx + prefix.length);
    } catch (e) {
      console.warn("[logo] Failed to parse logo URL:", e);
      return null;
    }
  }

  return raw;
}

const computeJobTotal = (job) => {
  if (!job) return 0;
  if (
    !Array.isArray(job.items) ||
    !job.items.length ||
    job.billingType === "flat"
  ) {
    return n(job.flatPrice, 0);
  }
  return job.items.reduce(
    (sum, it) => sum + n(it.qty, 0) * n(it.unitPrice ?? it.unit_price, 0),
    0
  );
};

const jobsToItems = (jobsList) => {
  const arr = Array.isArray(jobsList) ? jobsList : [];
  return arr.flatMap((job) => {
    const jobId = job.id ?? null;
    const jobName = limitText(job.name ?? "", "jobName");

    if (job.billingType === "flat" || !job.items?.length) {
      return [
        {
          id: rid(),
          description: jobName,
          qty: 1,
          unitPrice: n(job.flatPrice, 0),
          jobId,
          jobName,
        },
      ];
    }

    return job.items.map((it) => ({
      id: rid(),
      description: limitText(
        `${jobName} – ${limitText(it.description ?? "", "itemDescription")}`,
        "itemDescription"
      ),
      qty: n(it.qty, 0),
      unitPrice: n(it.unitPrice ?? it.unit_price, 0),
      jobId,
      jobName,
    }));
  });
};

/* -------------------- Small UI helpers -------------------- */

function JobCard({ job, onEdit, onRemove }) {
  const total = computeJobTotal(job);

  return (
    <div className="mb-3 rounded-xl border border-slate-200 bg-white p-3">
      <div className="mb-1 flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-900">{job.name}</div>
          {job.description && (
            <p className="mt-0.5 text-xs text-slate-500">{job.description}</p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onEdit}
            className="rounded-lg bg-blue-100 px-2 py-1 text-xs font-medium text-blue-600"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="rounded-lg bg-red-100 px-2 py-1 text-xs font-medium text-red-600"
          >
            Remove
          </button>
        </div>
      </div>

      {job.billingType === "flat" ? (
        <p className="mt-1 text-sm text-slate-800">
          Flat price: £{n(job.flatPrice, 0).toFixed(2)}
        </p>
      ) : (
        <div className="mt-2 rounded-lg bg-slate-50 p-2">
          {job.items?.map((it, idx) => {
            const qty = n(it.qty, 0);
            const price = n(it.unitPrice ?? it.unit_price, 0);
            const lineTotal = qty * price;
            return (
              <div
                key={idx}
                className="flex items-center justify-between text-xs"
              >
                <span className="flex-1 pr-2">{it.description}</span>
                <span className="w-10 text-right text-slate-500">{qty}</span>
                <span className="w-16 text-right text-slate-500">
                  £{price.toFixed(2)}
                </span>
                <span className="w-16 text-right font-semibold text-slate-800">
                  £{lineTotal.toFixed(2)}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <p className="mt-2 text-sm font-semibold text-slate-900">
        Job total: £{total.toFixed(2)}
      </p>
    </div>
  );
}

/* -------------------- Customer Editor Modal -------------------- */

function CustomerEditorModal({ open, initial, onClose, onSaved }) {
  const { notify } = useNotifications();
  const [name, setName] = useState(
    limitText(initial?.name ?? "", "customerName")
  );
  const [address, setAddress] = useState(
    limitText(initial?.address ?? "", "customerAddress")
  );
  const [city, setCity] = useState(
    limitText(initial?.city ?? "", "customerCity")
  );
  const [postCode, setPostCode] = useState(
    limitText(initial?.postCode ?? "", "customerPostCode")
  );
  const [phoneNumber, setPhoneNumber] = useState(
    limitText(initial?.phoneNumber ?? "", "phoneNumber")
  );
  const [email, setEmail] = useState(limitText(initial?.email ?? "", "email"));

  useEffect(() => {
    if (!open) return;
    setName(limitText(initial?.name ?? "", "customerName"));
    setAddress(limitText(initial?.address ?? "", "customerAddress"));
    setCity(limitText(initial?.city ?? "", "customerCity"));
    setPostCode(limitText(initial?.postCode ?? "", "customerPostCode"));
    setPhoneNumber(limitText(initial?.phoneNumber ?? "", "phoneNumber"));
    setEmail(limitText(initial?.email ?? "", "email"));
  }, [open, initial]);

  if (!open) return null;

  const handleSave = async () => {
    if (!name.trim()) {
      notify({
        type: "error",
        title: "Missing info",
        message: "Customer name is required.",
      });
      return;
    }

    try {
      const payload = clampFields(
        {
          name: name.trim(),
          address: address.trim() || undefined,
          city: city.trim() || undefined,
          postCode: postCode.trim() || undefined,
          phoneNumber: phoneNumber.trim() || undefined,
          email: email.trim() || undefined,
        },
        {
          name: INPUT_LIMITS.customerName,
          address: INPUT_LIMITS.customerAddress,
          city: INPUT_LIMITS.customerCity,
          postCode: INPUT_LIMITS.customerPostCode,
          phoneNumber: INPUT_LIMITS.phoneNumber,
          email: INPUT_LIMITS.email,
        }
      );

      const saved = initial
        ? await updateCustomer({ id: initial.id, ...payload })
        : await createCustomer(payload);

      onSaved(saved);
    } catch (e) {
      console.warn("Save customer error", e);
      notify({
        type: "error",
        title: "Save failed",
        message: "Failed to save customer details.",
      });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60">
      <div className="mx-4 max-h-[90vh] w-full max-w-md overflow-auto rounded-2xl bg-white p-4 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-medium text-red-500"
          >
            Cancel
          </button>
          <h2 className="text-sm font-semibold text-slate-900">
            {initial ? "Edit customer" : "Create customer"}
          </h2>
          <button
            type="button"
            onClick={handleSave}
            className="text-sm font-medium text-emerald-600"
          >
            Save
          </button>
        </div>

        <div className="space-y-3 text-sm">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">
              Name
            </label>
            <input
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
              value={name}
              onChange={(e) => setName(limitText(e.target.value, "customerName"))}
              placeholder="e.g. John Smith"
              maxLength={INPUT_LIMITS.customerName}
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">
              Address
            </label>
            <textarea
              className="h-20 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
              value={address}
              onChange={(e) =>
                setAddress(limitText(e.target.value, "customerAddress"))
              }
              placeholder="Street, house number"
              maxLength={INPUT_LIMITS.customerAddress}
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                City
              </label>
              <input
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
                value={city}
                onChange={(e) => setCity(limitText(e.target.value, "customerCity"))}
                maxLength={INPUT_LIMITS.customerCity}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                Post code
              </label>
              <input
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
                value={postCode}
                onChange={(e) =>
                  setPostCode(limitText(e.target.value, "customerPostCode"))
                }
                maxLength={INPUT_LIMITS.customerPostCode}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                Phone
              </label>
              <input
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
                value={phoneNumber}
                onChange={(e) =>
                  setPhoneNumber(limitText(e.target.value, "phoneNumber"))
                }
                maxLength={INPUT_LIMITS.phoneNumber}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                Email
              </label>
              <input
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
                value={email}
                onChange={(e) => setEmail(limitText(e.target.value, "email"))}
                autoComplete="email"
                maxLength={INPUT_LIMITS.email}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------- Job Editor Modal -------------------- */

function JobEditorModal({ open, initial, onClose, onSaved }) {
  const { notify } = useNotifications();
  const [name, setName] = useState(
    limitText(initial?.name ?? "", "jobName")
  );
  const [description, setDescription] = useState(
    limitText(initial?.description ?? "", "jobDescription")
  );
  const [billingType, setBillingType] = useState(
    initial?.billingType ?? "items"
  );
  const [flatPrice, setFlatPrice] = useState(
    initial?.flatPrice != null ? String(initial.flatPrice) : "0"
  );

  const initialItems =
    initial?.items && initial.items.length
      ? initial.items.map((it) => ({
          id: String(it.id ?? rid()),
          description: limitText(it.description ?? "", "itemDescription"),
          qty: n(it.qty, 1),
          unitPrice: n(it.unitPrice ?? it.unit_price, 0),
        }))
      : [newItem()];

  const [items, setItems] = useState(initialItems);

  useEffect(() => {
    if (!open) return;
    setName(limitText(initial?.name ?? "", "jobName"));
    setDescription(limitText(initial?.description ?? "", "jobDescription"));
    setBillingType(initial?.billingType ?? "items");
    setFlatPrice(initial?.flatPrice != null ? String(initial.flatPrice) : "0");
    const resetItems =
      initial?.items && initial.items.length
        ? initial.items.map((it) => ({
            id: String(it.id ?? rid()),
            description: limitText(it.description ?? "", "itemDescription"),
            qty: n(it.qty, 1),
            unitPrice: n(it.unitPrice ?? it.unit_price, 0),
          }))
        : [newItem()];
    setItems(resetItems);
  }, [open, initial]);

  if (!open) return null;

  const updateItem = (id, patch) => {
    const nextPatch = { ...patch };
    if (typeof nextPatch.description === "string") {
      nextPatch.description = limitText(
        nextPatch.description,
        "itemDescription"
      );
    }
    setItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, ...nextPatch } : it))
    );
  };
  const addItem = () => setItems((prev) => [...prev, newItem()]);
  const removeItem = (id) =>
    setItems((prev) => prev.filter((it) => it.id !== id));

  const handleSave = async () => {
    if (!name.trim()) {
      notify({
        type: "error",
        title: "Missing info",
        message: "Job name is required.",
      });
      return;
    }

    try {
      const payload = clampFields(
        {
          name: name.trim(),
          description: description.trim() || undefined,
          billingType,
          flatPrice:
            billingType === "flat" ? Number(flatPrice || 0) : undefined,
          items:
            billingType === "items"
              ? items.map((it) => ({
                  description: limitText(
                    it.description ?? "",
                    "itemDescription"
                  ),
                  qty: n(it.qty, 0),
                  unitPrice: n(it.unitPrice, 0),
                }))
              : [],
        },
        {
          name: INPUT_LIMITS.jobName,
          description: INPUT_LIMITS.jobDescription,
        }
      );

      const saved = await createJob(payload);
      onSaved(saved);
    } catch (e) {
      console.warn("Save job error", e);
      notify({
        type: "error",
        title: "Save failed",
        message: "Failed to save the job.",
      });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60">
      <div className="mx-4 max-h-[90vh] w-full max-w-2xl overflow-auto rounded-2xl bg-white p-4 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-medium text-red-500"
          >
            Cancel
          </button>
          <h2 className="text-sm font-semibold text-slate-900">
            {initial ? "Edit job" : "Create job"}
          </h2>
          <button
            type="button"
            onClick={handleSave}
            className="text-sm font-medium text-emerald-600"
          >
            Save
          </button>
        </div>

        <div className="space-y-3 text-sm">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">
              Job name
            </label>
            <input
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
              value={name}
              onChange={(e) => setName(limitText(e.target.value, "jobName"))}
              placeholder="e.g. Garden clearance"
              maxLength={INPUT_LIMITS.jobName}
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">
              Description
            </label>
            <textarea
              className="h-20 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
              value={description}
              onChange={(e) =>
                setDescription(limitText(e.target.value, "jobDescription"))
              }
              placeholder="Optional description"
              maxLength={INPUT_LIMITS.jobDescription}
            />
          </div>

          <div className="mb-1 flex rounded-xl bg-slate-100 p-1 text-xs font-medium">
            <button
              type="button"
              onClick={() => setBillingType("items")}
              className={[
                "flex-1 rounded-xl px-3 py-1.5",
                billingType === "items"
                  ? "bg-white text-slate-900"
                  : "text-slate-600",
              ].join(" ")}
            >
              Itemised
            </button>
            <button
              type="button"
              onClick={() => setBillingType("flat")}
              className={[
                "flex-1 rounded-xl px-3 py-1.5",
                billingType === "flat"
                  ? "bg-white text-slate-900"
                  : "text-slate-600",
              ].join(" ")}
            >
              Flat price
            </button>
          </div>

          {billingType === "flat" ? (
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                Flat price (£)
              </label>
              <input
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-right text-sm"
                value={flatPrice}
                onChange={(e) => setFlatPrice(e.target.value)}
                type="number"
                step="0.01"
              />
            </div>
          ) : (
            <div>
              <div className="mb-1 flex items-center justify-between">
                <span className="text-xs font-medium text-slate-600">
                  Items
                </span>
              </div>

              <div className="mb-1 flex text-[11px] font-semibold text-slate-500">
                <span className="flex-1 ">Description</span>
                <span className="w-16 text-left">Qty</span>
                <span className="w-24 text-left">Price</span>
                <span className="w-10 text-center">Del</span>
              </div>

              {items.map((it) => (
                <div key={it.id} className="mb-2 flex items-center text-xs">
                  <input
                    className="mr-2 flex-1 rounded-xl border border-slate-200 bg-slate-50 px-2 py-1"
                    placeholder="Description"
                    value={it.description}
                    onChange={(e) =>
                      updateItem(it.id, {
                        description: limitText(
                          e.target.value,
                          "itemDescription"
                        ),
                      })
                    }
                    maxLength={INPUT_LIMITS.itemDescription}
                  />
                  <input
                    className="mr-2 w-16 rounded-xl border border-slate-200 bg-slate-50 px-2 py-1 text-right"
                    type="number"
                    value={it.qty}
                    onChange={(e) =>
                      updateItem(it.id, { qty: n(e.target.value, 0) })
                    }
                  />
                  <input
                    className="mr-2 w-24 rounded-xl border border-slate-200 bg-slate-50 px-2 py-1 text-right"
                    type="number"
                    step="0.01"
                    value={it.unitPrice}
                    onChange={(e) =>
                      updateItem(it.id, {
                        unitPrice: n(e.target.value, 0),
                      })
                    }
                  />
                  <button
                    type="button"
                    onClick={() => removeItem(it.id)}
                    className="rounded-xl bg-red-100 px-3 py-1 text-xs font-medium text-red-600"
                  >
                    Del
                  </button>
                </div>
              ))}

              <button
                type="button"
                onClick={addItem}
                className="mt-1 rounded-xl bg-slate-900 px-3 py-1.5 text-xs font-medium text-white"
              >
                + Add item
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------- Customer Picker Modal -------------------- */

function CustomerPickerModal({
  open,
  onClose,
  customers = [],
  selectedCustomer,
  onSelect,
  onCreateNew,
  onEdit,
  maxQuickSelect,
  onUpgrade,
}) {
  if (!open) return null;

  const list = Array.isArray(customers) ? customers : [];
  const limit = Number.isFinite(maxQuickSelect)
    ? Math.max(maxQuickSelect, 0)
    : list.length;
  const selectableCustomers = list.slice(0, limit);
  const lockedCustomers = list.slice(limit);
  const hasCustomers = list.length > 0;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40">
      <div className="mx-4 max-h-[80vh] w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="text-xs font-medium text-red-500"
          >
            Cancel
          </button>
          <h3 className="text-sm font-semibold text-slate-900">
            Select customer
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-xs font-medium text-emerald-600"
          >
            Done
          </button>
        </div>

        <div className="max-h-[70vh] space-y-2 overflow-y-auto px-4 py-3 text-sm">
          <button
            type="button"
            onClick={onCreateNew}
            className="w-full rounded-xl border border-dashed border-slate-400 px-3 py-2 text-center text-xs font-medium text-slate-800 hover:bg-slate-50"
          >
            + Create new customer
          </button>

          {selectableCustomers.map((cust) => {
            const isSelected = selectedCustomer?.id === cust.id;
            return (
              <div
                key={cust.id}
                className={[
                  "rounded-xl border px-3 py-2",
                  isSelected
                    ? "border-slate-900 bg-slate-900/5"
                    : "border-slate-200",
                ].join(" ")}
              >
                <button
                  type="button"
                  onClick={() => onSelect(cust)}
                  className="flex w-full items-center justify-between text-left"
                >
                  <div className="flex-1 pr-2">
                    <div className="text-sm font-semibold text-slate-900">
                      {cust.name}
                    </div>
                    {cust.email && (
                      <div className="text-xs text-slate-500">{cust.email}</div>
                    )}
                    {(cust.address || cust.city || cust.postCode) && (
                      <div className="mt-1 text-xs text-slate-500">
                        {cust.address}
                        {cust.city ? `, ${cust.city}` : ""}
                        {cust.postCode ? `, ${cust.postCode}` : ""}
                      </div>
                    )}
                  </div>
                  <div className="text-xs">{isSelected ? "✓" : ""}</div>
                </button>

                <div className="mt-2 flex justify-end">
                  <button
                    type="button"
                    onClick={() => onEdit(cust)}
                    className="rounded-lg bg-blue-100 px-2 py-1 text-[11px] font-medium text-blue-600"
                  >
                    Edit
                  </button>
                </div>
              </div>
            );
          })}

          {!hasCustomers && (
            <p className="py-4 text-center text-xs text-slate-500">
              No saved customers yet. Create one above.
            </p>
          )}

          {hasCustomers && selectableCustomers.length === 0 && (
            <p className="py-4 text-center text-xs text-slate-500">
              Quick select is limited to {limit} customer
              {limit === 1 ? "" : "s"} on your current plan.
            </p>
          )}

          {lockedCustomers.length > 0 && (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-3 py-3 text-center text-xs text-slate-600">
              <p>
                Upgrade to Pro to quick-select {lockedCustomers.length} more
                saved customer{lockedCustomers.length === 1 ? "" : "s"}.
              </p>
              <button
                type="button"
                onClick={() => {
                  if (typeof onUpgrade === "function") onUpgrade();
                }}
                className="mt-2 rounded-full bg-black px-3 py-1 text-xs font-semibold text-white"
              >
                Upgrade plan
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------- Job Picker Modal -------------------- */

function JobPickerModal({
  open,
  onClose,
  jobs = [],
  selectedJobIds,
  onToggleJob,
  onApply,
  onCreateNew,
  onEdit,
  maxQuickSelect,
  onUpgrade,
}) {
  if (!open) return null;

  const list = Array.isArray(jobs) ? jobs : [];
  const limit = Number.isFinite(maxQuickSelect)
    ? Math.max(maxQuickSelect, 0)
    : list.length;
  const selectableJobs = list.slice(0, limit);
  const lockedJobs = list.slice(limit);
  const hasSelection = selectedJobIds.length > 0;
  const hasJobs = list.length > 0;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40">
      <div className="mx-4 w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="text-xs font-medium text-red-500"
          >
            Cancel
          </button>
          <h3 className="text-sm font-semibold text-slate-900">Select jobs</h3>
          <button
            type="button"
            onClick={onApply}
            disabled={!hasSelection}
            className={[
              "rounded-full px-3 py-1.5 text-xs font-medium shadow-sm",
              hasSelection
                ? "bg-slate-900 text-white hover:bg-slate-800"
                : "bg-slate-100 text-slate-400",
            ].join(" ")}
          >
            Use selected
          </button>
        </div>

        <div className="max-h-[70vh] space-y-2 overflow-y-auto px-4 py-3 text-sm">
          <button
            type="button"
            onClick={onCreateNew}
            className="mb-2 w-full rounded-xl border border-dashed border-slate-400 px-3 py-2 text-center text-xs font-medium text-slate-800 hover:bg-slate-50"
          >
            + Create new job
          </button>

          {selectableJobs.map((job) => {
            const isSelected = selectedJobIds.includes(job.id);
            const jobTotal = computeJobTotal(job);

            return (
              <div
                key={job.id}
                className={[
                  "rounded-xl border px-3 py-2",
                  isSelected
                    ? "border-slate-900 bg-slate-900/5"
                    : "border-slate-200",
                ].join(" ")}
              >
                <button
                  type="button"
                  onClick={() => onToggleJob(job.id)}
                  className="flex w-full items-start justify-between text-left"
                >
                  <div className="flex-1 pr-3">
                    <div className="text-sm font-semibold text-slate-900">
                      {job.name}
                    </div>
                    {job.description && (
                      <div className="mt-1 text-xs text-slate-500">
                        {job.description}
                      </div>
                    )}
                    <div className="mt-1 text-xs text-slate-600">
                      {job.billingType === "flat"
                        ? "Flat price"
                        : `${job.items?.length || 0} item(s)`}
                    </div>
                    <div className="mt-1 text-xs text-slate-700">
                      £{jobTotal.toFixed(2)}
                    </div>
                  </div>
                  <div className="text-xs font-semibold text-slate-900">
                    {isSelected ? "✓" : ""}
                  </div>
                </button>

                <div className="mt-2 flex justify-end">
                  <button
                    type="button"
                    onClick={() => onEdit(job)}
                    className="rounded-lg bg-blue-100 px-2 py-1 text-[11px] font-medium text-blue-600"
                  >
                    Edit
                  </button>
                </div>
              </div>
            );
          })}

          {!hasJobs && (
            <p className="py-4 text-center text-xs text-slate-500">
              You have no saved jobs yet. Create one above.
            </p>
          )}

          {lockedJobs.length > 0 && (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-3 py-3 text-center text-xs text-slate-600">
              <p>
                Quick select is limited to {limit} job
                {limit === 1 ? "" : "s"} on the Free plan. Upgrade to access{" "}
                {lockedJobs.length} more saved job
                {lockedJobs.length === 1 ? "" : "s"}.
              </p>
              <button
                type="button"
                onClick={() => {
                  if (typeof onUpgrade === "function") onUpgrade();
                }}
                className="mt-2 rounded-full bg-black px-3 py-1 text-xs font-semibold text-white"
              >
                View upgrade options
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------- Main Page -------------------- */

function InvoiceGeneratorPage({
  invoiceId,
  onBack,
  onSaved,
  planLimits,
  invoiceUsage = 0,
  refreshInvoiceUsage,
  onUpgrade,
}) {
  const { notify } = useNotifications();
  const [currentInvoiceId, setCurrentInvoiceId] = useState(
    invoiceId != null ? Number(invoiceId) : null
  );

  useEffect(() => {
    setCurrentInvoiceId(invoiceId != null ? Number(invoiceId) : null);
  }, [invoiceId]);

  const effectivePlan = planLimits ?? {};
  const isNewInvoice = currentInvoiceId == null;
  const invoiceLimit = effectivePlan.invoiceLimit ?? Infinity;
  const invoiceLimitFinite = Number.isFinite(invoiceLimit);
  const usedInvoices = Number.isFinite(invoiceUsage) ? invoiceUsage : 0;
  const invoicesRemaining = invoiceLimitFinite
    ? Math.max(invoiceLimit - usedInvoices, 0)
    : Infinity;
  const cannotCreateAnotherInvoice =
    invoiceLimitFinite && invoicesRemaining <= 0;
  const rawEditLimit = effectivePlan.editableFieldsBeforeNewInvoice;
  const editLimit =
    !isNewInvoice && Number.isFinite(rawEditLimit) ? rawEditLimit : Infinity;
  const quickCustomerLimit = effectivePlan.maxQuickSelectCustomers ?? Infinity;
  const quickJobLimit = effectivePlan.maxQuickSelectJobs ?? Infinity;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [logoUri, setLogoUri] = useState(null);
  const [companyName, setCompanyName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [postCode, setPostCode] = useState("");

  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(fmtDate());
  const [suppliedDate, setSuppliedDate] = useState(fmtDate());

  const [customerName, setCustomerName] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  const [customerCity, setCustomerCity] = useState("");
  const [customerPostCode, setCustomerPostCode] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");

  const [items, setItems] = useState([newItem(), newItem(), newItem()]);

  const [payee, setPayee] = useState("");
  const [sortCode, setSortCode] = useState("");
  const [accountNumber, setAccountNumber] = useState("");

  const [notes, setNotes] = useState("");
  const [terms, setTerms] = useState("");

  const [discount, setDiscount] = useState(0);
  const [deposit, setDeposit] = useState(0);

  const [styleChoice, setStyleChoice] = useState("formal");

  const [previewHtml, setPreviewHtml] = useState(null);
  const [livePreview, setLivePreview] = useState(true);

  const [jobs, setJobs] = useState([]);
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [jobPickerOpen, setJobPickerOpen] = useState(false);
  const [jobEditorOpen, setJobEditorOpen] = useState(false);
  const [jobEditorInitial, setJobEditorInitial] = useState(null);

  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);
  const [customerEditorOpen, setCustomerEditorOpen] = useState(false);
  const [customerEditorInitial, setCustomerEditorInitial] = useState(null);
  const [editedFields, setEditedFields] = useState([]);

  const invoiceItems = useMemo(() => {
    if (selectedJobs.length) {
      return jobsToItems(selectedJobs);
    }
    return items;
  }, [selectedJobs, items]);

  const subtotal = useMemo(
    () =>
      invoiceItems.reduce(
        (sum, it) => sum + n(it.qty, 0) * n(it.unitPrice, 0),
        0
      ),
    [invoiceItems]
  );

  const total = useMemo(
    () => Math.max(subtotal - (discount || 0) - (deposit || 0), 0),
    [subtotal, discount, deposit]
  );

  const updateItem = (id, patch) =>
    setItems((prev) =>
      prev.map((it) =>
        it.id === id
          ? {
              ...it,
              ...patch,
              description:
                typeof patch.description === "string"
                  ? limitText(patch.description, "itemDescription")
                  : it.description,
            }
          : it
      )
    );

  const addItem = () => {
    registerFieldEdit("items");
    setItems((prev) => [...prev, newItem()]);
  };

  const removeItem = (id) => {
    registerFieldEdit("items");
    setItems((prev) => prev.filter((it) => it.id !== id));
  };

  const rehydrateJobsFromItems = useCallback((normalizedItems) => {
    const groups = {};

    normalizedItems.forEach((it) => {
      let jobKey = null;
      let jobName = null;
      let itemDesc = null;

      if (it.jobId != null || it.jobName) {
        jobKey = String(it.jobId ?? it.jobName);
        jobName = (it.jobName ?? "").toString() || it.description;

        const desc = it.description || "";
        let idx = desc.indexOf(" – ");
        if (idx === -1) idx = desc.indexOf(" - ");
        if (idx > 0) {
          itemDesc = desc.slice(idx + 3).trim();
        } else {
          itemDesc = "";
        }
      } else {
        const desc = it.description || "";
        let idx = desc.indexOf(" – ");
        if (idx === -1) idx = desc.indexOf(" - ");
        if (idx <= 0) return;

        jobName = desc.slice(0, idx).trim();
        jobKey = jobName;
        itemDesc = desc.slice(idx + 3).trim();
      }

      if (!jobKey || !jobName) return;

      const existing = groups[jobKey] || {
        jobId: it.jobId ?? null,
        jobName,
        billingType: "items",
        flatPrice: 0,
        items: [],
      };

      if (!itemDesc) {
        existing.billingType = "flat";
        existing.flatPrice += n(it.qty, 0) * n(it.unitPrice, 0);
      } else {
        existing.items.push({
          description: itemDesc,
          qty: it.qty,
          unitPrice: it.unitPrice,
        });
      }

      groups[jobKey] = existing;
    });

    const keys = Object.keys(groups);
    if (!keys.length) return;

    const reconstructed = keys.map((key, index) => {
      const g = groups[key];
      const numericId = Number(key);
      const useId = Number.isFinite(numericId) ? numericId : -index - 1;
      return {
        id: useId,
        name: g.jobName,
        description: null,
        billingType: g.billingType,
        flatPrice: g.billingType === "flat" ? g.flatPrice : null,
        items:
          g.billingType === "items"
            ? g.items.map((it) => ({
                id: undefined,
                description: it.description,
                qty: it.qty,
                unitPrice: it.unitPrice,
              }))
            : [],
      };
    });

    setSelectedJobs(reconstructed);
  }, []);

  const applyDbInvoiceToState = useCallback(
    (db) => {
      setCurrentInvoiceId(
        db?.id != null && Number.isFinite(Number(db.id)) ? Number(db.id) : null
      );
      const safe = {
        invoiceNumber: limitText(db.invoiceNumber ?? "", "invoiceNumber"),
        invoiceDate: fmtDate(db.invoiceDate),
        suppliedDate: fmtDate(db.suppliedDate),
        customerName: limitText(db.customerName ?? "", "customerName"),
        customerAddress: limitText(db.customerAddress ?? "", "customerAddress"),
        customerCity: limitText(db.customerCity ?? "", "customerCity"),
        customerPostCode: limitText(
          db.customerPostCode ?? "",
          "customerPostCode"
        ),
        phoneNumber: limitText(db.phoneNumber ?? "", "phoneNumber"),
        email: limitText(db.email ?? "", "email"),
        payee: limitText(db.payee ?? "", "payee"),
        sortCode: limitText(db.sortCode ?? "", "sortCode"),
        accountNumber: limitText(db.accountNumber ?? "", "accountNumber"),
        notes: limitText(db.notes ?? "", "notes"),
        terms: limitText(db.terms ?? "", "terms"),
      };

      setInvoiceNumber(safe.invoiceNumber);
      setInvoiceDate(safe.invoiceDate);
      setSuppliedDate(safe.suppliedDate);

      setCustomerName(safe.customerName);
      setCustomerAddress(safe.customerAddress);
      setCustomerCity(safe.customerCity);
      setCustomerPostCode(safe.customerPostCode);
      setPhoneNumber(safe.phoneNumber);
      setEmail(safe.email);

      setNotes(safe.notes);
      setTerms(safe.terms);
      setPayee(safe.payee);
      setSortCode(safe.sortCode);
      setAccountNumber(safe.accountNumber);

      setDiscount(Number(db.discount) || 0);
      setDeposit(Number(db.deposit) || 0);

      const normalizedStyle =
        typeof db.style === "string"
          ? db.style === "legal"
            ? "formal"
            : db.style
          : null;
      setStyleChoice(
        normalizedStyle && STYLE_CHOICES.includes(normalizedStyle)
          ? normalizedStyle
          : "formal"
      );

      let parsedItems = [];
      try {
        if (Array.isArray(db.items)) {
          parsedItems = db.items;
        } else if (typeof db.items === "string") {
          parsedItems = JSON.parse(db.items);
        }
      } catch (e) {
        console.warn("Failed to parse items", e);
      }

      const norm =
        parsedItems && parsedItems.length
          ? normalizeItems(parsedItems)
          : [newItem(), newItem(), newItem()];

      setItems(norm);
      rehydrateJobsFromItems(norm);
    },
    [rehydrateJobsFromItems]
  );

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        try {
          const { data: user } = await supabase.auth.getUser();
          if (user?.user?.id) {
            const { data: profile } = await supabase
              .from("profiles")
              .select("*")
              .eq("id", user.user.id)
              .single();

            if (!cancelled && profile) {
              setCompanyName(limitText(profile.company_name ?? "", "companyName"));
              setAddress(limitText(profile.address ?? "", "address"));
              setCity(limitText(profile.city ?? "", "city"));
              setPostCode(limitText(profile.post_code ?? "", "postCode"));
              setPayee(limitText(profile.payee ?? "", "payee"));
              setSortCode(limitText(profile.sort_code ?? "", "sortCode"));
              setAccountNumber(
                limitText(profile.account_number ?? "", "accountNumber")
              );

              if (profile.logo_uri) {
                try {
                  const storagePath = extractStoragePath(profile.logo_uri);

                  if (!storagePath) {
                    if (!cancelled) setLogoUri(profile.logo_uri);
                  } else {
                    const { data: blob, error: logoError } =
                      await supabase.storage
                        .from(LOGO_BUCKET)
                        .download(storagePath);

                    if (logoError) {
                      console.warn(
                        "Error downloading logo from Supabase:",
                        logoError
                      );
                      if (!cancelled) setLogoUri(null);
                    } else if (!cancelled && blob) {
                      const dataUrl = await blobToDataUrl(blob);
                      if (!cancelled) setLogoUri(dataUrl);
                    }
                  }
                } catch (e) {
                  console.warn("Failed to load logo", e);
                  if (!cancelled) setLogoUri(null);
                }
              } else {
                if (!cancelled) setLogoUri(null);
              }
            }
          }
        } catch (e) {
          console.warn("Failed to load profile", e);
        }

        if (invoiceId) {
          try {
            const dbInv = await getInvoiceById(Number(invoiceId));
            if (!cancelled && dbInv) {
              applyDbInvoiceToState(dbInv);
            }
          } catch (e) {
            console.warn("Failed to load invoice", e);
          }
        } else {
          try {
            const { data, error } = await supabase.rpc("next_invoice_number");
            if (!cancelled && !error && data) {
              setInvoiceNumber(String(data));
            }
          } catch (e) {
            console.warn("Failed to get next invoice number", e);
          }
          if (!cancelled) {
            setInvoiceDate((prev) => fmtDate(prev));
            setSuppliedDate((prev) => fmtDate(prev));
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, [invoiceId, applyDbInvoiceToState]);

  useEffect(() => {
    const loadJobs = async () => {
      try {
        const all = await getJobsForUser();
        setJobs(all || []);
      } catch (e) {
        console.warn("Failed to load jobs", e);
      }
    };
    loadJobs();
  }, []);

  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const all = await getCustomersForUser();
        setCustomers(all || []);
      } catch (e) {
        console.warn("Failed to load customers", e);
      }
    };
    loadCustomers();
  }, []);

  const validate = useCallback(() => {
    if (!invoiceNumber.trim()) return "Invoice number is required.";
    if (!customerName.trim()) return "Customer name is required.";
    return null;
  }, [customerName, invoiceNumber]);

  const buildRecord = useCallback(
    () =>
      clampInvoiceRecord({
        id: currentInvoiceId != null ? Number(currentInvoiceId) : undefined,
        companyName,
        address,
        city,
        postCode,
        invoiceNumber,
        invoiceDate,
        suppliedDate,
        customerName,
        customerAddress,
        customerCity,
        customerPostCode,
        phoneNumber,
        email,
        items: invoiceItems,
        subtotal,
        discount,
        deposit,
        total,
        payee,
        sortCode,
        accountNumber,
        notes,
        terms,
        style: styleChoice,
        pdfPath: null,
        logoUri,
      }),
    [
      accountNumber,
      address,
      city,
      companyName,
      currentInvoiceId,
      customerAddress,
      customerCity,
      customerName,
      customerPostCode,
      deposit,
      discount,
      email,
      invoiceDate,
      invoiceItems,
      invoiceNumber,
      logoUri,
      notes,
      payee,
      phoneNumber,
      postCode,
      sortCode,
      styleChoice,
      subtotal,
      suppliedDate,
      terms,
      total,
    ]
  );

  useEffect(() => {
    setEditedFields([]);
  }, [currentInvoiceId]);

  const registerFieldEdit = useCallback(
    (fieldKey) => {
      if (!fieldKey || isNewInvoice) return;
      if (!Number.isFinite(editLimit)) return;
      setEditedFields((prev) => {
        if (prev.includes(fieldKey)) return prev;
        return [...prev, fieldKey];
      });
    },
    [editLimit, isNewInvoice]
  );

  const registerJobSelectionImpact = useCallback(
    (job) => {
      if (!job) return;
      const jobKey =
        job.id != null ? String(job.id) : job.name || `job-${rid()}`;

      registerFieldEdit(`job-${jobKey}-meta`);

      const jobLines =
        job.billingType === "flat" ||
        !Array.isArray(job.items) ||
        job.items.length === 0
          ? [{ __virtual: true }]
          : job.items;

      jobLines.forEach((_, idx) => {
        registerFieldEdit(`job-${jobKey}-line-${idx}-desc`);
        registerFieldEdit(`job-${jobKey}-line-${idx}-qty`);
        registerFieldEdit(`job-${jobKey}-line-${idx}-price`);
      });
    },
    [registerFieldEdit]
  );

  const applyCustomerToFields = useCallback(
    (cust, options = {}) => {
      if (!cust) return;
      const { trackAutoFill = false } = options;

      const maybeApply = (nextValue, currentValue, setter, fieldKey) => {
        if (trackAutoFill && nextValue !== currentValue) {
          registerFieldEdit(fieldKey);
        }
        setter(nextValue);
      };

      maybeApply(
        limitText(cust.name || "", "customerName"),
        customerName,
        setCustomerName,
        "customerName"
      );
      maybeApply(
        limitText(cust.address || "", "customerAddress"),
        customerAddress,
        setCustomerAddress,
        "customerAddress"
      );
      maybeApply(
        limitText(cust.city || "", "customerCity"),
        customerCity,
        setCustomerCity,
        "customerCity"
      );
      maybeApply(
        limitText(cust.postCode || "", "customerPostCode"),
        customerPostCode,
        setCustomerPostCode,
        "customerPostCode"
      );
      maybeApply(
        limitText(cust.phoneNumber || "", "phoneNumber"),
        phoneNumber,
        setPhoneNumber,
        "customerPhone"
      );
      maybeApply(
        limitText(cust.email || "", "email"),
        email,
        setEmail,
        "customerEmail"
      );
    },
    [
      customerName,
      customerAddress,
      customerCity,
      customerPostCode,
      phoneNumber,
      email,
      registerFieldEdit,
    ]
  );

  useEffect(() => {
    if (!customers.length || !customerName) return;

    setSelectedCustomer((prev) => {
      if (prev) return prev;
      const match = customers.find(
        (c) =>
          c.name === customerName &&
          (!customerAddress || c.address === customerAddress) &&
          (!email || c.email === email)
      );
      return match ?? null;
    });
  }, [customers, customerName, customerAddress, email]);

  const editCount = editedFields.length;
  const editLimitBreached = Number.isFinite(editLimit) && editCount > editLimit;
  const requiresNewInvoiceForEdits = !isNewInvoice && editLimitBreached;
  const editLimitBlocksSave =
    requiresNewInvoiceForEdits && cannotCreateAnotherInvoice;
  const newInvoiceBlocked = isNewInvoice && cannotCreateAnotherInvoice;
  const showEditLimitNotice = !isNewInvoice && Number.isFinite(editLimit);
  const editUsage = showEditLimitNotice ? Math.min(editCount, editLimit) : 0;
  const showInvoiceUsageBanner = isNewInvoice && invoiceLimitFinite;
  const planLimitLabel = invoiceLimitFinite ? invoiceLimit : "your plan";

  useEffect(() => {
    if (!livePreview) return;

    let cancelled = false;
    const timeoutId = setTimeout(async () => {
      const err = validate();
      if (err) {
        if (!cancelled) setPreviewHtml(null);
        return;
      }

      try {
        const record = buildRecord();
        const html = await createHtml(record, styleChoice);

        const zoomedHtml = html.includes("</head>")
          ? html.replace(
              "</head>",
              `<style>
@media screen {
  html, body {
    margin: 0;
    padding: 0;
    overflow-x: auto;
  }

  body {
    display: flex;
    justify-content: center;
    background: #ffffff;
  }

  .page {
    transform: none !important;
    transform-origin: initial;
    width: auto;
    max-width: 100%;
    box-shadow: none;
  }
}
</style></head>`
            )
          : html;

        if (!cancelled) {
          setPreviewHtml(zoomedHtml);
        }
      } catch (e) {
        console.warn("Live preview error", e);
        if (!cancelled) setPreviewHtml(null);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [
    livePreview,
    styleChoice,
    companyName,
    address,
    city,
    postCode,
    invoiceNumber,
    invoiceDate,
    suppliedDate,
    customerName,
    customerAddress,
    customerCity,
    customerPostCode,
    phoneNumber,
    email,
    invoiceItems,
    notes,
    terms,
    discount,
    deposit,
    payee,
    sortCode,
    accountNumber,
    logoUri,
    buildRecord,
    validate,
  ]);

  const saveRecord = async () => {
    const record = buildRecord();
    const creatingNewInvoice = record.id == null || isNewInvoice;

    const editCount = editedFields.length;
    const editsConsumeSlot =
      !creatingNewInvoice &&
      Number.isFinite(editLimit) &&
      editCount >= editLimit;

    const requiresSlot = creatingNewInvoice || editsConsumeSlot;

    if (requiresSlot && cannotCreateAnotherInvoice) {
      throw new Error("invoice-limit-reached");
    }

    let usedInvoiceSlot = false;

    if (creatingNewInvoice) {
      const payload = { ...record };
      delete payload.id;
      const newId = await saveInvoiceToDb(payload);
      record.id = newId;
      setCurrentInvoiceId(Number(newId));
      usedInvoiceSlot = true;
    } else {
      await updateInvoiceInDb({
        ...record,
        id: Number(record.id),
      });

      if (editsConsumeSlot) {
        usedInvoiceSlot = true;
        setEditedFields([]);
      }
    }

    return { record, usedInvoiceSlot };
  };

  const openHtmlInNewWindowAndPrint = (html) => {
    const win = window.open("", "_blank");
    if (!win) {
      notify({
        type: "warning",
        title: "Popup blocked",
        message: "Allow popups to download or print the invoice.",
      });
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
    win.print();
  };

  const ensureInvoiceCapacity = () => {
    if (newInvoiceBlocked) {
      notify({
        type: "warning",
        title: "Limit reached",
        message: `You’ve reached the limit of ${planLimitLabel} invoices on your plan. Upgrade to continue creating invoices.`,
      });
      if (typeof onUpgrade === "function") {
        onUpgrade();
      }
      return false;
    }

    if (editLimitBlocksSave) {
      notify({
        type: "warning",
        title: "No invoice slots left",
        message:
          "Further edits will consume another invoice slot, but you have none remaining.",
      });
      if (typeof onUpgrade === "function") {
        onUpgrade();
      }
      return false;
    }

    return true;
  };

  const handleGeneratePdf = async () => {
    const err = validate();
    if (err) {
      notify({
        type: "error",
        title: "Cannot generate invoice",
        message: err,
      });
      return;
    }
    if (!ensureInvoiceCapacity()) return;

    setSaving(true);
    try {
      const { record: saved, usedInvoiceSlot } = await saveRecord();
      if (usedInvoiceSlot && typeof refreshInvoiceUsage === "function") {
        await refreshInvoiceUsage();
      }
      const html = await createHtml(saved, styleChoice);
      openHtmlInNewWindowAndPrint(html);

      if (onSaved) onSaved();
    } catch (e) {
      console.warn("PDF error", e);
      notify({
        type: "error",
        title: "Failed to generate invoice",
        message: "Please refresh and try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveOnly = async () => {
    const err = validate();
    if (err) {
      notify({
        type: "error",
        title: "Cannot save invoice",
        message: err,
      });
      return;
    }
    if (!ensureInvoiceCapacity()) return;

    setSaving(true);
    try {
      const { usedInvoiceSlot } = await saveRecord();
      if (usedInvoiceSlot && typeof refreshInvoiceUsage === "function") {
        await refreshInvoiceUsage();
      }
      if (onSaved) onSaved();
    } catch (e) {
      console.warn("Save error", e);
      notify({
        type: "error",
        title: "Failed to save invoice",
        message: "Please refresh and try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  const selectedJobIds = useMemo(
    () => selectedJobs.map((j) => j.id),
    [selectedJobs]
  );

  const toggleJobSelection = (jobId) => {
    registerFieldEdit("jobs");
    setSelectedJobs((prev) => {
      const exists = prev.some((j) => j.id === jobId);
      if (exists) {
        return prev.filter((j) => j.id !== jobId);
      }
      const job = jobs.find((j) => j.id === jobId);
      if (!job) return prev;
      registerJobSelectionImpact(job);
      return [...prev, job];
    });
  };

  const applySelectedJobsToItems = () => {
    setJobPickerOpen(false);
  };

  if (loading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 shadow-sm">
          Loading invoice…
        </div>
      </div>
    );
  }

  const title = isNewInvoice
    ? "New invoice"
    : `Edit invoice #${invoiceNumber || currentInvoiceId}`;

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      {/* Top bar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{title}</h1>
          <p className="text-sm text-slate-500">
            Fill out the details, see a live preview, and print/save as PDF.
          </p>
        </div>
        <div className="flex gap-2">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50"
            >
              ← Back to invoices
            </button>
          )}
        </div>
      </div>

      {showInvoiceUsageBanner && (
        <div className="rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-900 shadow-sm">
          <div className="font-semibold">
            Free plan invoices remaining: {Math.max(invoicesRemaining, 0)} /{" "}
            {invoiceLimit}
          </div>
          {newInvoiceBlocked ? (
            <div className="mt-1 flex flex-wrap items-center gap-3">
              <span>You’ve reached the limit for this plan.</span>
              <button
                type="button"
                className="rounded-full bg-black px-3 py-1 text-xs font-semibold text-white"
                onClick={() => onUpgrade?.()}
              >
                Upgrade for more
              </button>
            </div>
          ) : (
            <div className="mt-1 text-xs text-amber-700">
              Creating this invoice will use 1 of your {invoiceLimit} slots.
            </div>
          )}
        </div>
      )}

      {showEditLimitNotice && (
        <div className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900 shadow-sm">
          <div className="font-semibold">
            Free edits used: {editUsage} / {editLimit}
          </div>
          {requiresNewInvoiceForEdits ? (
            <div className="mt-1 text-xs">
              Further changes will create a brand new invoice and use another
              slot.
            </div>
          ) : (
            <div className="mt-1 text-xs">
              You can change {Math.max(editLimit - editUsage, 0)} more field
              {editLimit - editUsage === 1 ? "" : "s"} before it counts as a new
              invoice.
            </div>
          )}
          {(requiresNewInvoiceForEdits || editLimitBlocksSave) && (
            <button
              type="button"
              className="mt-3 rounded-full bg-black px-3 py-1 text-xs font-semibold text-white"
              onClick={() => onUpgrade?.()}
            >
              Upgrade for unlimited edits
            </button>
          )}
        </div>
      )}

      {/* Main layout */}
      <div className="grid items-start gap-6 lg:grid-cols-2">
        {/* Left column: all inputs */}
        <div className="min-w-0 space-y-4">
          {/* Company + invoice info */}
          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div className="flex items-center gap-3">
                {logoUri ? (
                  <img
                    src={logoUri}
                    alt="Company logo"
                    className="h-10 w-10 rounded-lg border border-slate-200 bg-white object-contain"
                  />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 text-[10px] font-semibold text-slate-400">
                    LOGO
                  </div>
                )}
                <div>
                  <h2 className="text-sm font-semibold text-slate-900">
                    Company & invoice details
                  </h2>
                  <p className="text-xs text-slate-500">
                    Company info is pulled from your profile.
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-1">
                {STYLE_CHOICES.map((s) => {
                  const active = styleChoice === s;
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => {
                        registerFieldEdit("style");
                        setStyleChoice(s);
                      }}
                      className={[
                        "rounded-full px-3 py-1 text-xs font-medium capitalize",
                        active
                          ? "bg-slate-900 text-slate-50"
                          : "bg-slate-100 text-slate-700 hover:bg-slate-200",
                      ].join(" ")}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              {/* Company block */}
              <div className="space-y-2">
                <label className="block text-xs font-medium text-slate-600">
                  Company name
                  <input
                    type="text"
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-100 px-3 py-2 text-sm text-slate-500 cursor-not-allowed"
                    value={companyName}
                    readOnly
                    aria-readonly="true"
                  />
                </label>

                <label className="block text-xs font-medium text-slate-600">
                  Address
                  <input
                    type="text"
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-100 px-3 py-2 text-sm text-slate-500 cursor-not-allowed"
                    value={address}
                    readOnly
                    aria-readonly="true"
                  />
                </label>

                <div className="grid grid-cols-[2fr_1fr] gap-2">
                  <label className="text-xs font-medium text-slate-600">
                    City
                    <input
                      type="text"
                      className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-100 px-3 py-2 text-sm text-slate-500 cursor-not-allowed"
                      value={city}
                      readOnly
                      aria-readonly="true"
                    />
                  </label>
                  <label className="text-xs font-medium text-slate-600">
                    Post code
                    <input
                      type="text"
                      className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-100 px-3 py-2 text-sm text-slate-500 cursor-not-allowed"
                      value={postCode}
                      readOnly
                      aria-readonly="true"
                    />
                  </label>
                </div>
              </div>

              {/* Invoice meta */}
              <div className="space-y-2">
                <label className="block text-xs font-medium text-slate-600">
                  Invoice number
                  <input
                    type="text"
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-100 px-3 py-2 text-sm text-slate-500 cursor-not-allowed"
                    value={invoiceNumber}
                    readOnly
                    aria-readonly="true"
                  />
                </label>

                <div className="grid gap-2 sm:grid-cols-2">
                  <label className="text-xs font-medium text-slate-600">
                    Invoice date
                    <input
                      type="date"
                      className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                      value={invoiceDate}
                      onChange={(e) => {
                        registerFieldEdit("invoiceDate");
                        setInvoiceDate(e.target.value);
                      }}
                    />
                  </label>
                  <label className="text-xs font-medium text-slate-600">
                    Supplied / due date
                    <input
                      type="date"
                      className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                      value={suppliedDate}
                      onChange={(e) => {
                        registerFieldEdit("suppliedDate");
                        setSuppliedDate(e.target.value);
                      }}
                    />
                  </label>
                </div>
              </div>
            </div>
          </section>

          {/* Customer / billed-to */}
          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-slate-900">Billed to</h2>

            {/* Saved customer selector summary */}
            <div className="mt-3 flex items-start justify-between gap-3 rounded-xl bg-slate-50 px-3 py-2">
              <div className="text-xs text-slate-600">
                {selectedCustomer ? (
                  <>
                    <div className="text-sm font-semibold text-slate-900">
                      {selectedCustomer.name}
                    </div>
                    {selectedCustomer.email && (
                      <div className="text-xs text-slate-500">
                        {selectedCustomer.email}
                      </div>
                    )}
                    {(selectedCustomer.address ||
                      selectedCustomer.city ||
                      selectedCustomer.postCode) && (
                      <div className="mt-1 text-xs text-slate-500">
                        {selectedCustomer.address}
                        {selectedCustomer.city
                          ? `, ${selectedCustomer.city}`
                          : ""}
                        {selectedCustomer.postCode
                          ? `, ${selectedCustomer.postCode}`
                          : ""}
                      </div>
                    )}
                  </>
                ) : (
                  <span>
                    No saved customer selected. Fill manually or choose one.
                  </span>
                )}
              </div>

              <div className="flex shrink-0 gap-2">
                {selectedCustomer && (
                  <button
                    type="button"
                    onClick={() => setSelectedCustomer(null)}
                    className="rounded-full bg-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-300"
                  >
                    Clear
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setCustomerPickerOpen(true)}
                  className="rounded-full bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800"
                >
                  {selectedCustomer ? "Change customer" : "Select customer"}
                </button>
              </div>
            </div>

            {/* Always-editable fields */}
            <div className="mt-3 space-y-3">
              <label className="block text-xs font-medium text-slate-600">
                Customer name
                <input
                  type="text"
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                  value={customerName}
                  onChange={(e) => {
                    registerFieldEdit("customerName");
                    setCustomerName(limitText(e.target.value, "customerName"));
                  }}
                  placeholder="Client name"
                  maxLength={INPUT_LIMITS.customerName}
                />
              </label>

              <label className="block text-xs font-medium text-slate-600">
                Address
                <input
                  type="text"
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                  value={customerAddress}
                  onChange={(e) => {
                    registerFieldEdit("customerAddress");
                    setCustomerAddress(
                      limitText(e.target.value, "customerAddress")
                    );
                  }}
                  placeholder="Street, house number"
                  maxLength={INPUT_LIMITS.customerAddress}
                />
              </label>

              <div className="flex gap-2">
                <label className="flex-1 text-xs font-medium text-slate-600">
                  City
                  <input
                    type="text"
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                    value={customerCity}
                    onChange={(e) => {
                      registerFieldEdit("customerCity");
                      setCustomerCity(limitText(e.target.value, "customerCity"));
                    }}
                    maxLength={INPUT_LIMITS.customerCity}
                  />
                </label>
                <label className="flex-1 text-xs font-medium text-slate-600">
                  Post code
                  <input
                    type="text"
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                    value={customerPostCode}
                    onChange={(e) => {
                      registerFieldEdit("customerPostCode");
                      setCustomerPostCode(
                        limitText(e.target.value, "customerPostCode")
                      );
                    }}
                    maxLength={INPUT_LIMITS.customerPostCode}
                  />
                </label>
              </div>

              <div className="flex gap-2">
                <label className="flex-1 text-xs font-medium text-slate-600">
                  Phone
                  <input
                    type="tel"
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                    value={phoneNumber}
                    onChange={(e) => {
                      registerFieldEdit("customerPhone");
                      setPhoneNumber(
                        limitText(e.target.value, "phoneNumber")
                      );
                    }}
                    maxLength={INPUT_LIMITS.phoneNumber}
                  />
                </label>
                <label className="flex-1 text-xs font-medium text-slate-600">
                  Email
                  <input
                    type="email"
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                    value={email}
                    onChange={(e) => {
                      registerFieldEdit("customerEmail");
                      setEmail(limitText(e.target.value, "email"));
                    }}
                    maxLength={INPUT_LIMITS.email}
                  />
                </label>
              </div>

              <div className="flex gap-2">
                <label className="flex-1 text-xs font-medium text-slate-600">
                  Invoice date
                  <input
                    type="date"
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                    value={invoiceDate}
                    onChange={(e) => {
                      registerFieldEdit("invoiceDate");
                      setInvoiceDate(e.target.value);
                    }}
                  />
                </label>
                <label className="flex-1 text-xs font-medium text-slate-600">
                  Supplied / due date
                  <input
                    type="date"
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                    value={suppliedDate}
                    onChange={(e) => {
                      registerFieldEdit("suppliedDate");
                      setSuppliedDate(e.target.value);
                    }}
                  />
                </label>
              </div>
            </div>
          </section>

          {/* Jobs vs items */}
          {isNewInvoice || selectedJobs.length > 0 ? (
            <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-2 flex items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-slate-900">Jobs</h2>
                <button
                  type="button"
                  onClick={() => setJobPickerOpen(true)}
                  className="rounded-full bg-slate-900 px-3 py-1 text-xs font-medium text-white hover:bg-slate-800"
                >
                  + Add job
                </button>
              </div>

              {selectedJobs.length === 0 ? (
                <p className="text-xs text-slate-500">
                  No jobs added yet. Click &ldquo;Add job&rdquo; to select or
                  create a job. Line items will be generated automatically from
                  your jobs.
                </p>
              ) : (
                <div className="mt-2">
                  {selectedJobs.map((job) => (
                    <JobCard
                      key={job.id}
                      job={job}
                      onEdit={() => {
                        setJobEditorInitial(job);
                        setJobEditorOpen(true);
                      }}
                      onRemove={() => {
                        registerFieldEdit("jobs");
                        setSelectedJobs((prev) =>
                          prev.filter((j) => j.id !== job.id)
                        );
                      }}
                    />
                  ))}
                </div>
              )}
            </section>
          ) : (
            <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-slate-900">Items</h2>
                <button
                  type="button"
                  onClick={addItem}
                  className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-800 hover:bg-slate-200"
                >
                  + Add item
                </button>
              </div>

              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-xs text-slate-500">
                      <th className="px-2 py-1 text-left font-medium">
                        Description
                      </th>
                      <th className="px-2 py-1 text-right font-medium">Qty</th>
                      <th className="px-2 py-1 text-right font-medium">
                        Unit £
                      </th>
                      <th className="px-2 py-1 text-right font-medium">
                        Line total
                      </th>
                      <th className="px-2 py-1 text-center font-medium">Del</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => {
                      const line = n(it.qty, 0) * n(it.unitPrice, 0);
                      return (
                        <tr key={it.id} className="border-b border-slate-100">
                          <td className="px-2 py-1 align-top">
                            <input
                              type="text"
                              className="w-full rounded-xl border border-slate-200 bg-white px-2 py-1 text-sm"
                              placeholder="Description"
                              value={it.description}
                              onChange={(e) => {
                                registerFieldEdit("items");
                                updateItem(it.id, {
                                  description: limitText(
                                    e.target.value,
                                    "itemDescription"
                                  ),
                                });
                              }}
                              maxLength={INPUT_LIMITS.itemDescription}
                            />
                          </td>
                          <td className="px-2 py-1 align-top">
                            <input
                              type="number"
                              className="w-20 rounded-xl border border-slate-200 bg-white px-2 py-1 text-right text-sm"
                              value={it.qty}
                              onChange={(e) => {
                                registerFieldEdit("items");
                                updateItem(it.id, {
                                  qty: n(e.target.value, 0),
                                });
                              }}
                            />
                          </td>
                          <td className="px-2 py-1 align-top">
                            <input
                              type="number"
                              step="0.01"
                              className="w-24 rounded-xl border border-slate-200 bg-white px-2 py-1 text-right text-sm"
                              value={it.unitPrice}
                              onChange={(e) => {
                                registerFieldEdit("items");
                                updateItem(it.id, {
                                  unitPrice: n(e.target.value, 0),
                                });
                              }}
                            />
                          </td>
                          <td className="px-2 py-1 text-right align-top text-sm text-slate-900">
                            £{line.toFixed(2)}
                          </td>
                          <td className="px-2 py-1 text-center align-top">
                            <button
                              type="button"
                              onClick={() => removeItem(it.id)}
                              className="rounded-full bg-red-50 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-100"
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {items.length === 0 && (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-2 py-4 text-center text-xs text-slate-500"
                        >
                          No items yet. Add at least one line.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Payment info */}
          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-slate-900">
              Payment details
            </h2>

            <div className="mt-3 space-y-3 text-xs">
              <label className="block font-medium text-slate-600">
                Payee
                <input
                  type="text"
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                  value={payee}
                  onChange={(e) => {
                    registerFieldEdit("payee");
                    setPayee(limitText(e.target.value, "payee"));
                  }}
                  maxLength={INPUT_LIMITS.payee}
                />
              </label>

              <div className="flex gap-2">
                <label className="flex-1 font-medium text-slate-600">
                  Sort code
                  <input
                    type="text"
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                    value={sortCode}
                    onChange={(e) => {
                      registerFieldEdit("sortCode");
                      setSortCode(limitText(e.target.value, "sortCode"));
                    }}
                    maxLength={INPUT_LIMITS.sortCode}
                  />
                </label>
                <label className="flex-1 font-medium text-slate-600">
                  Account number
                  <input
                    type="text"
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                    value={accountNumber}
                    onChange={(e) => {
                      registerFieldEdit("accountNumber");
                      setAccountNumber(
                        limitText(e.target.value, "accountNumber")
                      );
                    }}
                    maxLength={INPUT_LIMITS.accountNumber}
                  />
                </label>
              </div>
            </div>
          </section>

          {/* Totals */}
          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-slate-900">Summary</h2>

            <dl className="mt-3 space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <dt className="text-slate-500">Subtotal</dt>
                <dd className="font-medium text-slate-900">
                  £{subtotal.toFixed(2)}
                </dd>
              </div>

              <div className="flex items-center justify-between">
                <dt className="text-slate-500">Discount</dt>
                <dd>
                  <input
                    type="number"
                    step="0.01"
                    className="w-28 rounded-xl border border-slate-200 bg-white px-2 py-1 text-right text-sm"
                    value={discount}
                    onChange={(e) => {
                      registerFieldEdit("discount");
                      setDiscount(n(e.target.value, 0));
                    }}
                  />
                </dd>
              </div>

              <div className="flex items-center justify-between">
                <dt className="text-slate-500">Deposit</dt>
                <dd>
                  <input
                    type="number"
                    step="0.01"
                    className="w-28 rounded-xl border border-slate-200 bg-white px-2 py-1 text-right text-sm"
                    value={deposit}
                    onChange={(e) => {
                      registerFieldEdit("deposit");
                      setDeposit(n(e.target.value, 0));
                    }}
                  />
                </dd>
              </div>

              <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
                <dt className="text-sm font-semibold text-slate-900">
                  Total due
                </dt>
                <dd className="text-lg font-semibold text-slate-900">
                  £{total.toFixed(2)}
                </dd>
              </div>
            </dl>
          </section>

          {/* Notes & terms */}
          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-slate-900">
              Notes & terms
            </h2>

            <div className="mt-3 space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-600">
                  Notes
                </label>
                <textarea
                  className="mt-1 h-20 w-full resize-none rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                  value={notes}
                  onChange={(e) => {
                    registerFieldEdit("notes");
                    setNotes(limitText(e.target.value, "notes"));
                  }}
                  placeholder="Any extra info you want to show on the invoice."
                  maxLength={INPUT_LIMITS.notes}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600">
                  Terms
                </label>
                <textarea
                  className="mt-1 h-24 w-full resize-none rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                  value={terms}
                  onChange={(e) => {
                    registerFieldEdit("terms");
                    setTerms(limitText(e.target.value, "terms"));
                  }}
                  placeholder="Payment terms, due date, etc."
                  maxLength={INPUT_LIMITS.terms}
                />
              </div>
            </div>
          </section>

          {/* Actions */}
          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={handleSaveOnly}
                disabled={saving}
                className="inline-flex items-center justify-center rounded-2xl bg-slate-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? "Saving…" : "Save invoice"}
              </button>
              <button
                type="button"
                onClick={handleGeneratePdf}
                disabled={saving}
                className="inline-flex items-center justify-center rounded-2xl bg-black px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving
                  ? "Generating…"
                  : isNewInvoice
                  ? "Save & open PDF"
                  : "Save changes & open PDF"}
              </button>
            </div>
          </section>
        </div>

        {/* Right column: big sticky live preview */}
        <div className="min-w-0 space-y-4 lg:sticky lg:top-4">
          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-900">
                Live preview
              </h2>
              <label className="flex items-center gap-1 text-xs text-slate-600">
                <input
                  type="checkbox"
                  className="h-3 w-3"
                  checked={livePreview}
                  onChange={(e) => setLivePreview(e.target.checked)}
                />
                Auto-update
              </label>
            </div>

            <div className="mt-3 h-[70vh] overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
              {previewHtml ? (
                <iframe
                  title="Invoice preview"
                  className="h-full w-full"
                  srcDoc={previewHtml}
                />
              ) : (
                <div className="flex h-full items-center justify-center px-4 text-center text-xs text-slate-500">
                  Fill in the invoice details to see a live preview here.
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() =>
                previewHtml && openHtmlInNewWindowAndPrint(previewHtml)
              }
              disabled={!previewHtml}
              className="mt-3 inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Open full-page preview / print
            </button>
          </section>
        </div>
      </div>

      {/* Customer picker modal */}
      <CustomerPickerModal
        open={customerPickerOpen}
        onClose={() => setCustomerPickerOpen(false)}
        customers={customers}
        selectedCustomer={selectedCustomer}
        onSelect={(cust) => {
          setSelectedCustomer(cust);
          applyCustomerToFields(cust, { trackAutoFill: true });
          setCustomerPickerOpen(false);
        }}
        onCreateNew={() => {
          setCustomerPickerOpen(false);
          setCustomerEditorInitial(null);
          setCustomerEditorOpen(true);
        }}
        onEdit={(cust) => {
          setCustomerPickerOpen(false);
          setCustomerEditorInitial(cust);
          setCustomerEditorOpen(true);
        }}
        maxQuickSelect={quickCustomerLimit}
        onUpgrade={onUpgrade}
      />

      {/* Customer editor modal */}
      <CustomerEditorModal
        open={customerEditorOpen}
        initial={customerEditorInitial}
        onClose={() => {
          setCustomerEditorOpen(false);
          setCustomerEditorInitial(null);
        }}
        onSaved={async (savedCustomer) => {
          try {
            const all = await getCustomersForUser();
            setCustomers(all || []);
          } catch (e) {
            console.warn("Failed to refresh customers", e);
          }

          setSelectedCustomer(savedCustomer);
          applyCustomerToFields(savedCustomer, { trackAutoFill: true });

          setCustomerEditorInitial(null);
          setCustomerEditorOpen(false);
        }}
      />

      {/* Job picker modal */}
      <JobPickerModal
        open={jobPickerOpen}
        onClose={() => setJobPickerOpen(false)}
        jobs={jobs}
        selectedJobIds={selectedJobIds}
        onToggleJob={toggleJobSelection}
        onApply={applySelectedJobsToItems}
        onCreateNew={() => {
          setJobPickerOpen(false);
          setJobEditorInitial(null);
          setJobEditorOpen(true);
        }}
        onEdit={(job) => {
          setJobPickerOpen(false);
          setJobEditorInitial(job);
          setJobEditorOpen(true);
        }}
        maxQuickSelect={quickJobLimit}
        onUpgrade={onUpgrade}
      />

      {/* Job editor modal */}
      <JobEditorModal
        open={jobEditorOpen}
        initial={jobEditorInitial}
        onClose={() => {
          setJobEditorOpen(false);
          setJobEditorInitial(null);
        }}
        onSaved={async (savedJob) => {
          try {
            const all = await getJobsForUser();
            setJobs(all || []);
          } catch (e) {
            console.warn("Failed to refresh jobs", e);
          }

          setSelectedJobs((prev) => {
            registerFieldEdit("jobs");
            if (!jobEditorInitial) {
              return [...prev, savedJob];
            }
            return prev.map((j) =>
              j.id === jobEditorInitial.id ? savedJob : j
            );
          });

          setJobEditorInitial(null);
          setJobEditorOpen(false);
        }}
      />
    </div>
  );
}

export default InvoiceGeneratorPage;
